/**
 * Generic history helper — factors the snapshot-based lazy-batching undo
 * pattern locked in the Timeline store (DEC-048) into a reusable module
 * so every non-timeline store can adopt undo/redo without duplicating
 * boilerplate.
 *
 * Contract per store:
 *   - Store holds one canonical `state` object (usually the persisted
 *     shape). Every setter goes through a single `setState(next)` sink.
 *   - Store imports `createHistory<State>({ read, write })` at module
 *     init, where `read` returns the current state and `write(next)`
 *     applies + persists it.
 *   - Every mutation calls `history.checkpoint()` BEFORE the setState.
 *     Inside a `beginBatch`/`endBatch` scope, only the first checkpoint
 *     inside that batch actually captures — subsequent mutations no-op.
 *     A begin/end pair with no mutation inside costs nothing.
 *   - Drag-like flows call `beginBatch()` on drag-start + `endBatch()`
 *     on drag-end so the whole drag collapses into one undo entry.
 *
 * Cross-store coordination (Ctrl+Z everywhere):
 *   Every `createHistory` registers itself in a module-level list so the
 *   global key handler can walk stores in registration order. Not the
 *   final story (a per-store LRU-by-timestamp approach is nicer), but
 *   sufficient to get behavior parity across a session.
 *
 * Visible chrome + toast (2026-08-11). `undoDepth`/`redoDepth` are real
 * Solid signals (not plain closures reading a number) specifically so a
 * Header button can reactively enable/disable without polling — this
 * matters now that something actually consumes them (previously exported
 * but unread by any component). `globalUndo`/`globalRedo` are the shared
 * "walk the registry, act on the most-recently-edited store, announce
 * what happened" entry point — both the keyboard handler below AND the
 * Header's undo/redo buttons call these, so behavior can't drift between
 * the two triggers. A store with its OWN scoped keyboard handler (Timeline
 * — see timeline/store.ts / TimelineEditor.tsx) bypasses this global walk
 * on purpose (editing Timeline should always undo Timeline, not whatever
 * else was touched last) but still calls `announceHistoryResult` directly
 * so the toast stays consistent regardless of which path triggered it.
 */

import { createSignal } from 'solid-js';
import { pushToast } from '../ui/toast.ts';

const DEFAULT_MAX_ENTRIES = 100;

export interface HistoryConfig<S> {
  /** Return the current state. */
  read: () => S;
  /** Apply the new state (setSignal + persist to localStorage). */
  write: (next: S) => void;
  /** Optional identifier for logging / debugging. */
  name?: string;
  /** Max entries per stack. Defaults to 100. */
  maxEntries?: number;
  /** Called when a new undo entry is pushed (not on undo / redo): `undo/editOrder.ts` keeps one order across
   *  histories with it. */
  onCheckpoint?: () => void;
}

export interface HistoryHandle<S> {
  /** Snapshot pre-state onto the undo stack + drop redo history.
   *  Inside a batch, only the FIRST checkpoint captures. */
  checkpoint(): void;
  /** Open a batch scope. Every mutation until `endBatch` becomes ONE
   *  undo entry. Empty batches cost nothing. Re-entrant. */
  beginBatch(): void;
  endBatch(): void;
  /** Undo — restores the topmost pre-state; pushes current onto redo.
   *  Returns true if an entry was popped. */
  undo(): boolean;
  /** Redo — inverse of undo. */
  redo(): boolean;
  /** Clear both stacks. Useful when the containing editor closes. */
  clear(): void;
  /** Current undo/redo stack depths (for chrome affordances). */
  undoDepth(): number;
  redoDepth(): number;
  /** Wall-clock ms of the most recent checkpoint. Used by the global
   *  Ctrl+Z router to pick the most-recently-edited store. */
  lastEditMs(): number;
  /** Store identifier passed to createHistory (or `<anonymous>`). */
  readonly name: string;
  /** Name the NEXT edit (2026-11-02, the keyframe editing audit: "Undid a change to ..." said nothing about what came
   *  back). Taken by the next checkpoint (inside a batch, by the batch's), dropped at the end of the current task if no
   *  edit came. The toast after an undo or redo says it. */
  label(text: string): void;
  /** What the editor shows when an edit is made (its selection), kept with each step so an undo can put back what was
   *  selected before the edit and a redo what was selected after it. Null to stop. */
  setContext(read: (() => unknown) | null): void;
  /** The step the last undo or redo moved: its label and the context to show again. */
  lastMoved(): { label?: string; context?: unknown } | null;
  /** Internal: unregister from the global list (test tear-down). */
  __unregister(): void;
}

const registry: HistoryHandle<unknown>[] = [];

/** Get every registered history handle, ordered by most-recently-edited
 *  first. The global Ctrl+Z handler walks this list to find the target
 *  store: undo the one the user just edited, not the oldest one. */
export function historyByMostRecent(): HistoryHandle<unknown>[] {
  return [...registry].sort((a, b) => b.lastEditMs() - a.lastEditMs());
}

export function createHistory<S>(cfg: HistoryConfig<S>): HistoryHandle<S> {
  const name = cfg.name ?? '<anonymous>';
  const maxEntries = cfg.maxEntries ?? DEFAULT_MAX_ENTRIES;

  type Entry = { state: S; label?: string; context?: unknown };
  const undoStack: Entry[] = [];
  const redoStack: Entry[] = [];
  let lastEditMs = 0;
  let pendingLabel: string | undefined;
  let readContext: (() => unknown) | null = null;
  let moved: { label?: string; context?: unknown } | null = null;
  const context = (): unknown => (readContext ? readContext() : undefined);

  // Reactive mirrors of the two stack depths — plain array `.length`
  // reads wouldn't notify a Solid component of a change. Synced right
  // after every stack mutation below.
  const [undoDepthSig, setUndoDepthSig] = createSignal(0);
  const [redoDepthSig, setRedoDepthSig] = createSignal(0);
  function syncDepthSignals(): void {
    setUndoDepthSig(undoStack.length);
    setRedoDepthSig(redoStack.length);
  }

  let batchDepth = 0;
  let batchCaptured = false;
  let batchPreState: S | null = null;
  let batchPreContext: unknown;

  function pushCheckpoint(pre: S, ctx: unknown): void {
    undoStack.push({ state: pre, ...(pendingLabel ? { label: pendingLabel } : {}), context: ctx });
    pendingLabel = undefined;
    if (undoStack.length > maxEntries) {
      undoStack.splice(0, undoStack.length - maxEntries);
    }
    if (redoStack.length > 0) redoStack.length = 0;
    lastEditMs = Date.now();
    syncDepthSignals();
    cfg.onCheckpoint?.();
  }

  const handle: HistoryHandle<S> = {
    name,
    checkpoint(): void {
      if (batchDepth > 0) {
        if (!batchCaptured) {
          pushCheckpoint(batchPreState ?? cfg.read(), batchPreState ? batchPreContext : context());
          batchCaptured = true;
          batchPreState = null;
        }
        return;
      }
      pushCheckpoint(cfg.read(), context());
    },
    beginBatch(): void {
      if (batchDepth === 0) {
        batchPreState = cfg.read();
        batchPreContext = context();
        batchCaptured = false;
      }
      batchDepth++;
    },
    endBatch(): void {
      if (batchDepth > 0) batchDepth--;
      if (batchDepth === 0) {
        batchPreState = null;
        batchCaptured = false;
      }
    },
    undo(): boolean {
      if (undoStack.length === 0) return false;
      const prev = undoStack.pop()!;
      redoStack.push({ state: cfg.read(), ...(prev.label ? { label: prev.label } : {}), context: context() });
      moved = { ...(prev.label ? { label: prev.label } : {}), context: prev.context };
      cfg.write(prev.state);
      lastEditMs = Date.now();
      syncDepthSignals();
      return true;
    },
    redo(): boolean {
      if (redoStack.length === 0) return false;
      const next = redoStack.pop()!;
      undoStack.push({ state: cfg.read(), ...(next.label ? { label: next.label } : {}), context: context() });
      moved = { ...(next.label ? { label: next.label } : {}), context: next.context };
      cfg.write(next.state);
      lastEditMs = Date.now();
      syncDepthSignals();
      return true;
    },
    clear(): void {
      undoStack.length = 0;
      redoStack.length = 0;
      batchDepth = 0;
      batchPreState = null;
      batchCaptured = false;
      syncDepthSignals();
    },
    undoDepth: undoDepthSig,
    redoDepth: redoDepthSig,
    lastEditMs(): number { return lastEditMs; },
    label(text: string): void {
      pendingLabel = text;
      queueMicrotask(() => { if (pendingLabel === text) pendingLabel = undefined; });
    },
    setContext(read: (() => unknown) | null): void { readContext = read; },
    lastMoved() { return moved; },
    __unregister(): void {
      const ix = registry.indexOf(handle as HistoryHandle<unknown>);
      if (ix >= 0) registry.splice(ix, 1);
    },
  };

  registry.push(handle as HistoryHandle<unknown>);
  return handle;
}

/** Global Ctrl+Z / Ctrl+Y / Ctrl+Shift+Z handler.
 *
 *  Falls through when focus is inside an `<input>` / `<textarea>` /
 *  `contenteditable` element so browser-native input undo works there.
 *  Picks the most-recently-edited store to undo against; if that store
 *  has nothing to undo (already at floor), walks down the list until
 *  it finds one that does. */
export function installGlobalUndoRedoHandler(): () => void {
  function isEditableTarget(el: EventTarget | null): boolean {
    if (!(el instanceof HTMLElement)) return false;
    const tag = el.tagName.toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if (el.isContentEditable) return true;
    return false;
  }
  function onKeyDown(e: KeyboardEvent): void {
    if (!(e.ctrlKey || e.metaKey)) return;
    if (isEditableTarget(e.target)) return;
    const key = e.key.toLowerCase();
    if (key === 'z' && !e.shiftKey) {
      if (globalUndo()) { e.preventDefault(); }
    } else if ((key === 'y') || (key === 'z' && e.shiftKey)) {
      if (globalRedo()) { e.preventDefault(); }
    }
  }
  window.addEventListener('keydown', onKeyDown);
  return () => window.removeEventListener('keydown', onKeyDown);
}

/** True when ANY registered store has something to undo/redo — what the
 *  Header's undo/redo buttons disable on. Plain functions, NOT a
 *  module-scope `createMemo` — a memo built here would compute its
 *  initial value (and lock in its dependency list) the moment this
 *  module finishes loading, which is BEFORE any store has called
 *  `createHistory()` (stores import from this file, so this file's
 *  top-level code — including a memo's first run — executes first).
 *  `registry` would be empty at that instant, `.some()` on an empty
 *  array touches no signals, and the memo would freeze at `false`
 *  forever with zero tracked dependencies. Caught via the chrome's own
 *  smoke: the Undo button never enabled after a real edit (2026-08-11).
 *  A plain function sidesteps this — it's evaluated live, inside
 *  whatever reactive scope calls it (the Header's JSX), by which point
 *  every store has long since registered, so the signal reads it makes
 *  are tracked correctly by that caller's own reactivity. */
export function canUndoAny(): boolean { return registry.some((h) => h.undoDepth() > 0); }
export function canRedoAny(): boolean { return registry.some((h) => h.redoDepth() > 0); }

/** Terser labels than the raw `name` passed to `createHistory` for the
 *  toast — "projects" reads as internal jargon, "Layout" is what the
 *  operator calls it. Falls back to the raw name for anything unmapped
 *  so a future store doesn't need to touch this file to work correctly,
 *  just to read nicely. */
const DISPLAY_NAMES: Record<string, string> = {
  matrix: 'Matrix',
  macros: 'Macros',
  sequencers: 'Sequencer',
  fixtures: 'Fixtures',
  projects: 'Layout',
  bridges: 'Bridges',
  timeline: 'Timeline',
  song: 'Song',
};

function displayName(name: string): string {
  return DISPLAY_NAMES[name] ?? name;
}

/** Toast after an undo/redo action. Shared by the global keyboard/button
 *  path below AND any store with its own scoped keyboard handler
 *  (Timeline — its handler bypasses `globalUndo`/`globalRedo` on purpose,
 *  see the file header, but still calls this directly so the toast is
 *  identical either way). No-ops on `ok: false` — nothing happened, so
 *  nothing to announce ("Undo" with an empty stack is a normal keypress,
 *  not an event worth a toast). */
export function announceHistoryResult(kind: 'undo' | 'redo', ok: boolean, name: string): void {
  if (!ok) return;
  const verb = kind === 'undo' ? 'Undid' : 'Redid';
  pushToast('ok', `${verb}: ${displayName(name)}`, 1800);
}

/** Walk stores most-recently-edited first, undo the first one with
 *  anything to undo, announce the result. Shared by the keyboard handler
 *  and the Header's undo button so the two triggers can't drift. */
export function globalUndo(): boolean {
  for (const h of historyByMostRecent()) {
    if (h.undoDepth() > 0) {
      const ok = h.undo();
      announceHistoryResult('undo', ok, h.name);
      return ok;
    }
  }
  return false;
}

export function globalRedo(): boolean {
  for (const h of historyByMostRecent()) {
    if (h.redoDepth() > 0) {
      const ok = h.redo();
      announceHistoryResult('redo', ok, h.name);
      return ok;
    }
  }
  return false;
}
